// ============================================
// OLA Cosmetics — Scroll Zoom
// Large editorial images breathe as they pass through the viewport: widest when
// entering or leaving, settled at their natural size when centred, with a small
// amount of lag so they trail the scroll instead of being nailed to it.
//
// JS only writes two custom properties (--oz-s, --oz-y). The stylesheet owns the
// transform, so a section can still layer its own hover scale on top by
// multiplying --oz-s instead of overwriting the whole transform.
//
// Visibility is computed inside the frame loop rather than through an
// IntersectionObserver: other scripts on this theme re-create image nodes, and
// per-node state kept outside the DOM does not survive that. The rect is needed
// every frame anyway, so this costs nothing.
// ============================================
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Anything already driven by another effect, plus product cards — a grid of
  // thumbnails all breathing at once reads as noise, not craft.
  var SELF_SKIP =
    '[data-parallax], [data-no-scroll-zoom], .text-reveal__img, .tilt-3d, ' +
    '.rh-pc__img, .fp-card__img, .product-card__img';
  var ANCESTOR_SKIP =
    '[data-parallax], [data-no-scroll-zoom], .split-section__media, .hover-zoom, ' +
    '.product-gallery__slide, .rh-footer__watermark, .fp-card, .product-card, ' +
    // the UGC reel strip already scales each card as it reaches the centre;
    // a second scale on the image inside it just muddies both. The Instagram grid
    // is nine tiles at once — the same "busy grid" problem as product cards.
    '.rh-pc__link, .ola-box__thumb, .rh-ugc__item, .instagram-grid-container';

  // Images inside a group breathe as ONE: the scale is driven by the group's position,
  // not each image's own. Three tiles of different heights stacked in a grid otherwise
  // each hit their own centre at a different moment and the section looks unsynced —
  // and the small ones can fall under the size gate while the big one passes, so only
  // part of the group animates at all.
  var GROUP_SEL = '[data-zoom-group], .rh-values__grid';

  // rhodeskin.com — the reference Guy pointed at — swings its editorial images
  // between scale 1.0 and 1.2. Anything under ~10% simply does not read as motion.
  var coarse = window.matchMedia('(pointer: coarse)').matches;
  var BASE = 1.015;                  // resting size — a sliver of headroom for the lag
  // 0.22 stacked on top of a cover crop hid half the lifestyle frame at the extremes.
  // 0.16 still reads clearly and keeps the crop where the photography can carry it.
  var AMP      = coarse ? 0.16 : 0.20; // symmetric sections: settle at centre
  var AMP_SEC  = coarse ? 0.24 : 0.30; // sections that pull back instead
  // The cover's travel is keyed to the viewport, not the pointer: on a phone the frame
  // is 9:16 against a 2:3 source, so ~16% of the width is already gone before any zoom.
  // 0.36 on top of that showed 43% of the photo at page load. Narrow screens get a
  // fraction of it, and the breakpoint matches the stylesheet's.
  var narrow = window.innerWidth < 768;
  // 0.16 was set before the pivot moved onto her face, when the zoom cropped from the
  // frame centre and ate her hair. With transform-origin at 38% the window starts at
  // 0.38 - 0.38/s, so the top of her hair at 8% survives up to scale 1.267. 1.015 + 0.22
  // tops out at 1.235 — a travel you can actually see, with margin to spare.
  var AMP_HERO = narrow ? 0.22 : (coarse ? 0.36 : 0.44); // the cover carries the most travel
  var AMP_FIT  = coarse ? 0.11 : 0.14; // object-fit: contain — see restScale()
  // Packshots pull back like the rest, but their ceiling comes from the photo, not from
  // taste — and it is different for every photo. The gift-box shot holds its subject
  // between 13% and 90% of the height; the STARTER bottle runs 4% to 86% and has barely
  // any room above it. One number cannot serve both, so fitPackshot() below reads each
  // file and works out how far THAT image can travel. This is only the ask.
  var AMP_PACK = coarse ? 0.22 : 0.28;
  var PACK_SEL = '.ola-box__img, .rh-spot__left .rh-spot__full-img, [data-zoom-packshot]';
  var VMAX = coarse ? 0.016 : 0.025; // extra zoom while scrolling fast
  var LAG  = coarse ? 9 : 14;        // px the image trails the scroll by, capped by headroom
  var VREF = 55;                     // px per 60Hz frame that counts as "fast"
  var EASE = 0.12;                   // approach per 60Hz frame — lower is heavier
  var MARGIN = 0.15;                 // start easing this much before it enters
  // Pull-back reads better than a symmetric pump wherever the image shares the frame
  // with copy and controls: it holds full size while you are reading, then releases as
  // you scroll on. A pump barely moves through the middle, which is exactly when the
  // section is being looked at, so it goes unnoticed.
  // How far a photograph may be magnified past its own pixels before the zoom is
  // costing more in sharpness than it is buying in motion.
  //
  // Measured on the live cover: the file is 1320x742, the card renders at 1476 CSS px,
  // and the hero zoom took it to 2148 — 1.63x its own width, which is the smear that
  // is visible on the homepage. Every request above 1320 returns the same 129KB file,
  // so there are no more pixels to fetch; the only lever left is to stop stretching
  // them. At 1.30 the same cover peaks at 1717px instead of 2148 and still travels
  // enough to read as motion.
  //
  // 1.10 rather than 1.30 after measuring what the frame could contribute. The hero
  // frame is 1476x661 against a 1320x742 file: the ratio is already the efficient way
  // round, cover scales by width alone, and nothing is cropped from the sides. Matching
  // the frame to the photo's proportions would have improved sharpness by exactly 1.00x
  // — nothing. The frame was never the problem; the magnification on top of it was.
  //
  // 1.00 once Guy chose absolute sharpness over a full-bleed cover: no photograph is
  // magnified past its own pixels at all. Paired with the frame cap in the hero, the
  // cover now renders at 1:1. Everything else measured between 0.23x and 0.88x, with
  // 2048px files in 474px slots, so their zoom is untouched by this.
  //
  // This is adaptive on purpose. A large photograph keeps the full effect and only a
  // small one is held back, so replacing the cover with a bigger file restores the
  // original zoom on its own, with nothing to remember and nothing to edit.
  var MAX_UPSCALE = 1.00;

  var ZOOM_OUT_SEL = '.rh-hero__img, [data-zoom-out]';
  var HERO_SEL = '.rh-hero__img, [data-zoom-hero]';
  var FR   = 1000 / 60;              // the frame the tuning above is expressed in

  var items = [];
  var seen = new WeakSet();
  var lastY = window.pageYOffset;
  var vel = 0;
  var running = false;
  var lastMove = 0;
  var lastT = 0;

  // The scale a still image at this position settles to — no velocity term.
  //
  // Raised cosine rather than a power curve: its slope is zero at BOTH ends, so the
  // image neither snaps as it enters the viewport nor arrives hard at its resting
  // size, and most of the travel happens across the middle where it is actually being
  // looked at. A power curve puts the steepest change at the edges, which is the part
  // that reads as jerky.
  // A `contain` image already touches the frame at scale 1, so there is nothing to grow
  // into — scaling it up is exactly what was cropping the packshots. It pulls back by
  // SUBTRACTING instead: full size while it is centred and being read, receding into the
  // frame as you scroll on. Same zoom-out to the eye, and it cannot crop at any amplitude.
  function restScale(r, mode, amp, this_el_h) {
    var vh = window.innerHeight;
    var p;                                       // 0 = widest, 1 = settled

    if (mode === 'hero') {
      // The cover is already on screen at scroll 0, so anchoring it to the viewport
      // centre meant it sat still for the first couple of hundred pixels before it
      // started moving — which reads as the effect starting late. Drive it off the
      // scroll position instead: it begins pulling back on the very first pixel.
      // offsetHeight, not the rect: r.height is this element's TRANSFORMED box, so the
      // distance the pull-back is measured over grew and shrank with the scale it was
      // producing. The layout height is the frame the reader actually scrolls past.
      p = window.pageYOffset / Math.max(1, this_el_h || r.height);
    } else if (mode === 'out' || mode === 'contain') {
      // Across the WHOLE pass, from the moment it enters at the bottom to the moment it
      // leaves at the top. Holding it at full size until the centre left a mid-page
      // section motionless for most of the time it was on screen — which is exactly why
      // THE BOX read as having no effect at all.
      p = (vh - r.top) / (r.height + vh);
    } else {
      // symmetric: settles at the centre, widest at either edge
      var d = Math.abs((r.top + r.height / 2 - vh / 2) / vh);
      if (d > 1) d = 1;
      return BASE + amp * (1 - Math.cos(Math.PI * d)) / 2;
    }

    if (p < 0) p = 0; else if (p > 1) p = 1;
    // The cover eases OUT only. A raised cosine is flat at BOTH ends, and that flat
    // start is what read as the effect beginning late — the first tenth of the travel
    // barely moved. A quarter sine leaves at full slope and still settles softly.
    var f = mode === 'hero' ? Math.sin(p * Math.PI / 2)
                            : (1 - Math.cos(Math.PI * p)) / 2;
    if (mode === 'contain') return 1 - amp * f;
    return BASE + amp * (1 - f);
  }

  // ── Per-photo fit ───────────────────────────────────────────────────────────
  // A packshot is an object on a plain backdrop, so its subject is exactly the rows that
  // hold contrast. Find them, and two things follow: where to aim the crop, and how far
  // the image can scale before the crop reaches the product.
  //
  // Only packshots. A lifestyle frame has content edge to edge, so this test would read
  // zero slack and flatten the effect — those keep their tuned amplitude instead.
  function fitPackshot(img, frame) {
    var N = 72, c, g, d;
    try {
      c = document.createElement('canvas'); c.width = N; c.height = N;
      g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(img, 0, 0, N, N);
      d = g.getImageData(0, 0, N, N).data;
    } catch (e) { return null; }               // tainted canvas — fall back to the tuned amp

    // a plain, even border is what makes it a packshot
    var i, x, y, bMin = 255, bMax = 0;
    for (x = 0; x < N; x++) {
      var edges = [x, (N - 1) * N + x, x * N, x * N + N - 1];
      for (i = 0; i < 4; i++) {
        var v = (d[edges[i] * 4] + d[edges[i] * 4 + 1] + d[edges[i] * 4 + 2]) / 3;
        if (v < bMin) bMin = v;
        if (v > bMax) bMax = v;
      }
    }
    if (bMax - bMin > 60) return null;         // busy edges → lifestyle, leave it alone

    var top = -1, bot = -1;
    for (y = 0; y < N; y++) {
      var mn = 255, mx = 0;
      for (x = 0; x < N; x++) {
        var p = (y * N + x) * 4, w = (d[p] + d[p + 1] + d[p + 2]) / 3;
        if (w < mn) mn = w;
        if (w > mx) mx = w;
      }
      if (mx - mn > 55) { if (top < 0) top = y; bot = y; }
    }
    if (top < 0 || bot <= top) return null;

    var a = top / N, b = (bot + 1) / N;         // subject span, as fractions of the height
    var slack = a + (1 - b);
    if (slack < 0.04) return null;

    // Aim the crop so it eats the two margins in proportion — the subject ends up
    // centred in whatever window is left, which is the most travel the photo allows.
    var posY = a / slack;

    // At scale s the window covers (natRatio / frameRatio) / s of the source height.
    // It still holds the subject while that is at least (1 - slack).
    var cover = Math.max(frame.width / img.naturalWidth, frame.height / img.naturalHeight);
    var atOne = frame.height / (img.naturalHeight * cover);
    var maxScale = atOne / (1 - slack + 0.02);  // 2% of the source kept as margin

    return { posY: posY, maxScale: maxScale };
  }

  // Cheapest tests first — this runs over every image on a debounced scroll, so
  // an image that will never qualify has to be rejected without a style lookup.
  // Hold the zoom to what the file can actually carry. naturalWidth is 0 until the
  // image decodes, so a lazily-loaded one is re-capped on load rather than guessed at.
  // The srcset is the authority on how many pixels the file really has. naturalWidth
  // reports whatever bitmap the browser is holding, and a stale cached copy reported
  // 2269 for a cover the CDN serves at 1320 — enough to lift this cap right off and
  // leave the photograph magnified after everything else had been done to sharpen it.
  function sourceWidth(img) {
    var list = (img.getAttribute('srcset') || '').match(/(\d+)w/g);
    if (list && list.length) {
      var max = 0;
      for (var i = 0; i < list.length; i++) {
        var v = parseInt(list[i], 10);
        if (v > max) max = v;
      }
      if (max) return max;
    }
    return img.naturalWidth;
  }

  function capToResolution(img, ref, amp) {
    var nw = sourceWidth(img);
    var boxW = (ref.getBoundingClientRect().width) || img.offsetWidth;
    if (!nw || !boxW) {
      if (!img.__ozRecap) {
        img.__ozRecap = true;
        img.addEventListener('load', function () {
          for (var i = 0; i < items.length; i++) {
            if (items[i].el === img) {
              items[i].amp = capToResolution(img, items[i].ref, items[i].amp);
              break;
            }
          }
        }, { once: true });
      }
      return amp;
    }
    var allowed = (nw * MAX_UPSCALE) / boxW;   // the largest total scale this file affords
    if (BASE + amp > allowed) amp = Math.max(0, allowed - BASE);
    return amp;
  }

  function qualifies(img, skipSize) {
    var r = img.getBoundingClientRect();
    if (!skipSize && (r.width < 300 || r.height < 260)) return false;
    if (r.width < 80 || r.height < 80) return false;

    // The wrapper gets overflow:hidden, so it must hold nothing but images —
    // otherwise a badge or caption positioned outside the frame would be clipped.
    var p = img.parentElement;
    if (!p) return false;
    for (var i = 0; i < p.children.length; i++) {
      var tag = p.children[i].tagName;
      // <source> inside a <picture> paints nothing, so it cannot be clipped and must not
      // disqualify the frame — switching the hero to <picture> silently dropped it here.
      if (tag !== 'IMG' && tag !== 'SOURCE') return false;
    }

    // and the image must actually fill it, or scaling would reveal the wrapper.
    // offsetWidth/Height, not the rect: the rect of an element this script has already
    // scaled is the TRANSFORMED box, so on a re-scan a zoomed image could fail its own
    // fill test and be dropped permanently. Offsets are layout, before any transform.
    if (img.offsetWidth < p.offsetWidth * 0.98 || img.offsetHeight < p.offsetHeight * 0.98) return false;

    if (img.matches(SELF_SKIP) || img.closest(ANCESTOR_SKIP)) return false;
    var fit = window.getComputedStyle(img).objectFit;
    return fit === 'cover' || fit === 'contain';
  }

  function collect() {
    var i;
    for (i = items.length - 1; i >= 0; i--) {
      if (!items[i].el.isConnected) items.splice(i, 1);
    }
    var imgs = document.querySelectorAll('img');
    for (i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (seen.has(img) || !qualifies(img)) continue;

      // One image qualifying pulls in every sibling in its group, size gate waived,
      // so a grid never animates in part.
      var group = img.closest(GROUP_SEL);
      if (!group) { register(img, null); continue; }
      var kin = group.querySelectorAll('img');
      for (var k = 0; k < kin.length; k++) {
        if (!seen.has(kin[k]) && qualifies(kin[k], true)) register(kin[k], group);
      }
    }
  }

  function register(img, group) {
    seen.add(img);
    img.classList.add('ola-zoom');
    img.parentElement.classList.add('ola-zoom-frame');

    // Seed it at the size its current position calls for, not at rest — an image
    // that is already half-way up the page must not animate into place on load.
    var ref = group || img;
    var contain = window.getComputedStyle(img).objectFit === 'contain';
    var pack = !contain && img.matches(PACK_SEL);
    var mode = contain ? 'contain'
             : img.matches(HERO_SEL) ? 'hero'
             : (pack || img.matches(ZOOM_OUT_SEL)) ? 'out'
             : 'sym';
    var amp = contain ? AMP_FIT
            : pack ? AMP_PACK
            : mode === 'hero' ? AMP_HERO
            : mode === 'out' ? AMP_SEC : AMP;

    if (pack) {
      var fit = fitPackshot(img, img.parentElement.getBoundingClientRect());
      if (fit) {
        img.style.objectPosition = '50% ' + (fit.posY * 100).toFixed(1) + '%';
        if (BASE + amp > fit.maxScale) amp = Math.max(0, fit.maxScale - BASE);
      } else {
        amp = Math.min(amp, 0.12);             // could not read it — stay conservative
      }
    }
    amp = capToResolution(img, ref, amp);

    var it = {
      el: img, ref: ref, mode: mode, amp: amp, fit: contain,
      s: restScale(ref.getBoundingClientRect(), mode, amp, img.offsetHeight), y: 0, live: false
    };
    img.style.setProperty('--oz-s', it.s.toFixed(4));
    img.style.setProperty('--oz-y', '0px');
    items.push(it);
  }

  function frame(now) {
    // Everything below is tuned in 60Hz frames, so scale each step by the real elapsed
    // time. Without this the effect is literally half as strong on a 120Hz screen and
    // eases twice as fast — which is most of what "not smooth" turns out to be.
    var dt = lastT ? Math.min(now - lastT, 50) : FR;
    if (dt < 4) dt = 4;              // guard the division below on a 240Hz panel
    lastT = now;
    var step = dt / FR;
    var ease = 1 - Math.pow(1 - EASE, step);

    var y = window.pageYOffset;
    var raw = (y - lastY) / step;                  // px per 60Hz frame
    vel += (raw - vel) * (1 - Math.pow(0.75, step));
    lastY = y;
    if (Math.abs(vel) > 0.3) lastMove = now;

    var vh = window.innerHeight;
    var pad = vh * MARGIN;
    var vSigned = Math.max(-1, Math.min(1, vel / VREF));
    var vAbs = Math.abs(vSigned);
    var settling = false;

    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var r = it.ref.getBoundingClientRect();       // group rect when grouped
      var visible = r.bottom > -pad && r.top < vh + pad && r.width > 0;

      // Off-screen items track their position instead of freezing at their last value.
      // Parking once and stopping meant an element that scrolled past and was then
      // approached again from the other side came back holding the wrong end of the
      // range, and visibly eased out of it on screen. The rect is already in hand, so
      // keeping it honest costs one write.
      if (!visible) {
        if (it.live) { it.live = false; it.el.style.willChange = ''; }
        var park = restScale(r, it.mode, it.amp, it.el.offsetHeight);
        if (Math.abs(park - it.s) > 0.0005) {
          it.s = park; it.y = 0;
          it.el.style.setProperty('--oz-s', park.toFixed(4));
          it.el.style.setProperty('--oz-y', '0px');
        }
        continue;
      }
      if (!it.live) { it.live = true; it.el.style.willChange = 'transform'; }

      var ts = restScale(r, it.mode, it.amp, it.el.offsetHeight) + (it.fit ? 0 : VMAX * vAbs);
      it.s += (ts - it.s) * ease;

      // The image may only trail the scroll by as much as the current zoom hides.
      // Faster scrolling means more zoom, which buys more lag — it regulates itself,
      // and an edge can never be exposed.
      var room = Math.max(0, (it.fit ? 1 - it.s : it.s - 1)) * it.el.getBoundingClientRect().height / 2;
      var ty = -vSigned * LAG;
      if (ty > room) ty = room; else if (ty < -room) ty = -room;
      it.y += (ty - it.y) * ease;

      it.el.style.setProperty('--oz-s', it.s.toFixed(4));
      it.el.style.setProperty('--oz-y', it.y.toFixed(2) + 'px');

      if (Math.abs(ts - it.s) > 0.0004 || Math.abs(ty - it.y) > 0.05) settling = true;
    }

    // Keep running while anything is still easing, and for a moment after the
    // last scroll so the settle-out is smooth. Otherwise stop — no idle rAF.
    running = settling || (now - lastMove < 500);
    if (running) requestAnimationFrame(frame);
  }

  function kick() {
    if (running) return;
    running = true;
    lastT = 0;                       // a resumed loop must not see the whole idle gap as dt
    lastY = window.pageYOffset;
    lastMove = performance.now();
    requestAnimationFrame(frame);
  }

  // Clearing items by hand skipped the only place will-change is cleared, so every
  // dropped element leaked a permanent compositor layer.
  function reset() {
    for (var i = 0; i < items.length; i++) items[i].el.style.willChange = '';
    items.length = 0;
    seen = new WeakSet();
  }

  function refresh() { collect(); kick(); }

  // A lazy image measures 0×0 until it is laid out, so one pass at DOMContentLoaded
  // misses most of the page. Re-scan as the reader moves down instead.
  var pending = null;
  function refreshSoon() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; refresh(); }, 250);
  }

  refresh();
  window.addEventListener('scroll', function () { kick(); refreshSoon(); }, { passive: true });
  window.addEventListener('resize', function () {
    var n = window.innerWidth < 768;
    if (n !== narrow) { narrow = n; AMP_HERO = n ? 0.16 : (coarse ? 0.36 : 0.44); reset(); }
    refreshSoon();
  }, { passive: true });
  window.addEventListener('load', refresh);
  document.addEventListener('load', refreshSoon, true); // img load — does not bubble

  // Sections rendered later (theme editor, lazy sections, image swaps) join in.
  if (window.MutationObserver) {
    new MutationObserver(refreshSoon).observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('shopify:section:load', refresh);
})();
